import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Search, Book, MessageCircle } from 'lucide-react';
import { sections } from '../../data/Docs/Sections';

function Docs() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    // Detect if the user is on a mobile device
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsMobile(true);
        setShowAlert(true); // Show alert on mobile
      } else {
        setIsMobile(false);
        setShowAlert(false); // Hide alert on desktop
      }
    };

    // Run the resize handler on component mount and window resize
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const closeAlert = () => {
    setShowAlert(false);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const filteredSections = sections.map(section => ({
    ...section,
    subsections: section.subsections.filter(subsection =>
      subsection.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subsection.content.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.subsections.length > 0
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Custom mobile alert */}
      {showAlert && (
        <div className="fixed top-0 left-0 w-full bg-red-600 text-white p-4 shadow-lg z-50">
          <div className="flex justify-between items-center">
            <span className="font-bold">¡Atención!</span>
            <button onClick={closeAlert} className="text-xl">&times;</button>
          </div>
          <p>Esta página está mejor optimizada para desktop. Para una mejor experiencia, visita desde un dispositivo de escritorio.</p>
        </div>
      )}

      {/* Mobile menu button */}
      <button 
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="fixed top-20 right-4 z-50 p-2 bg-navy-900 text-white rounded-lg shadow-lg md:hidden"
      >
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* WhatsApp Contact Button */}
      <a
        href="https://wa.me/YOUR_PHONE_NUMBER"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-red-600 text-white p-4 rounded-full shadow-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2 group"
      >
        <MessageCircle size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-200 ease-in-out whitespace-nowrap">
          ¿Necesitas ayuda?
        </span>
      </a>

      {/* Sidebar */}
      <nav className={`fixed top-0 left-0 h-full w-72 bg-white shadow-lg transform transition-transform duration-200 ease-in-out z-40 overflow-y-auto ${isMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Book size={24} className="text-navy-900" />
            <h1 className="text-2xl font-bold text-navy-900">Documentación</h1>
          </div>

          {/* Search bar */}
          <div className="relative mb-6">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar documentación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          {/* Navigation */}
          <ul className="space-y-2">
            {filteredSections.map((section) => (
              <li key={section.id} className="mb-2">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="flex items-center w-full p-2 rounded-lg transition-colors text-center hover:bg-gray-100"
                >
                  <section.icon size={20} className="text-navy-900 mr-3" />
                  <span className="text-sm font-medium text-navy-900">{section.title}</span>
                  <ChevronDown
                    size={16}
                    className={`ml-auto transition-transform ${expandedSections.includes(section.id) ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedSections.includes(section.id) && (
                  <ul className="mt-2 space-y-2 pl-6">
                    {section.subsections.map((subsection) => (
                      <li key={subsection.id}>
                        <button
                          onClick={() => scrollToSection(subsection.id)}
                          className={`flex items-center w-full p-2 rounded-lg transition-colors text-left
                            ${activeSection === subsection.id
                              ? 'bg-red-100 text-red-600'
                              : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                          <subsection.icon size={16} className="mr-2 text-navy-900" />
                          <span className="text-sm">{subsection.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Main content */}
      <main className="md:ml-24 p-6">
        <div className="lg:max-w-3xl mx-auto">
          {sections.map((section) => (
            <div key={section.id} className="mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-6 flex items-center">
                <section.icon size={28} className="text-navy-900 mr-3" />
                {section.title}
              </h2>
              {section.subsections.map((subsection) => (
                <section
                  key={subsection.id}
                  data-section={subsection.id}
                  className="bg-white rounded-lg shadow-sm p-8 mb-6"
                >
                  <div className="flex items-center mb-6">
                    <subsection.icon size={24} className="text-navy-900 mr-3" />
                    <h3 className="text-2xl font-bold lg:text-center text-navy-900">{subsection.title}</h3>
                  </div>
                  <div className="prose prose-navy max-w-5xl">
                    <pre className="bg-gray-50 w-full p-4 rounded-lg text-sm ">{subsection.content}</pre>
                  </div>
                </section>
              ))}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default Docs;
